import {
  reviewConfigSchema,
  type ReviewConfig,
  commentTypesSchema,
  commentSeveritiesSchema,
  defaultOrgConfig,
} from '@mesa/core/config';
import type { ReviewCommentType, ReviewCommentSeverity } from '@mesa/core/review';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useZero } from '@/lib/zero';
import { useCurrentOrg } from '@/services/org';

// Configuration for form sections - making it data-driven
const FORM_CONFIG = {
  switches: [
    {
      key: 'autoReview' as const,
      label: 'Auto Review',
      description: 'Mesa will automatically review pull requests when they are opened, published from draft, or when new commits are pushed.',
    },
    {
      key: 'syncCommentsToGitHub' as const,
      label: 'Sync Generated Review Comments to GitHub',
      description:
        'If turned off, generated inline comments will not be posted to GitHub and will only be visible in Mesa. We will still post the high-level review summary to GitHub.',
    },
  ],
  checkboxSections: [
    {
      key: 'commentTypes' as const,
      title: 'Comment Types',
      description: 'Select which types of review comments the assistant should generate.',
      options: Object.keys(commentTypesSchema.shape) as ReviewCommentType[],
    },
    {
      key: 'commentSeverities' as const,
      title: 'Comment Severities',
      description: 'Select which severity levels of review comments the assistant should generate.',
      options: Object.keys(commentSeveritiesSchema.shape) as ReviewCommentSeverity[],
    },
  ],
} as const;

export const Route = createFileRoute('/_authed/_layout/$owner/settings/agents')({
  component: AgentsSettingsComponent,
});

function AgentsSettingsComponent() {
  const z = useZero();
  const { owner } = Route.useParams();
  const { currentOrg } = useCurrentOrg();

  const [configRecord] = useZeroQuery(z.query.OrgConfigTable.where('owner', owner).one());

  // Parse with Zod defaults
  const config = useMemo(
    () => reviewConfigSchema.parse(configRecord?.config.reviewAgent ?? defaultOrgConfig.reviewAgent),
    [configRecord?.config]
  );

  // Single update handler with better error handling
  const updateConfig = async (updates: Partial<ReviewConfig>): Promise<void> => {
    try {
      const newConfig = reviewConfigSchema.parse({ ...config, ...updates });

      await z.mutate.OrgConfigTable.upsert({
        owner: currentOrg.account.login,
        installationId: currentOrg.id,
        config: {
          ...(configRecord?.config ?? defaultOrgConfig),
          reviewAgent: newConfig,
        },
      });
    } catch (error) {
      console.error('Validation error:', error);
      toast.error('Failed to update setting. Please try again.');
    }
  };

  // Specific handlers for different field types
  const updateBooleanField =
    (field: keyof Pick<ReviewConfig, 'autoReview' | 'syncCommentsToGitHub'>) => (checked: boolean) =>
      updateConfig({ [field]: checked });

  const updateNestedBooleanField =
    (section: keyof Pick<ReviewConfig, 'commentTypes' | 'commentSeverities'>, key: string) => (checked: boolean) => {
      const currentSection = config[section] as Record<string, boolean>;
      updateConfig({
        [section]: { ...currentSection, [key]: checked },
      } as Partial<ReviewConfig>);
    };

  // Helper to update autoApprove subfields
  const updateAutoApprove = (updates: Partial<ReviewConfig['autoApprove']>) => {
    updateConfig({
      autoApprove: {
        ...config.autoApprove,
        ...updates,
      },
    });
  };

  const updateAutoApproveThreshold = (key: keyof ReviewConfig['autoApprove']['threshold']) => (value: number | '') => {
    const num = typeof value === 'number' ? value : value === '' ? undefined : Number(value);
    updateAutoApprove({
      threshold: {
        ...config.autoApprove.threshold,
        [key]: num,
      },
    });
  };

  // Label management helpers
  const addLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (config.autoApprove.labels.includes(trimmed)) return;
    updateAutoApprove({ labels: [...config.autoApprove.labels, trimmed] });
  };

  const removeLabel = (label: string) => {
    updateAutoApprove({ labels: config.autoApprove.labels.filter((l) => l !== label) });
  };

  const [labelInput, setLabelInput] = useState('');

  return (
    <div className="size-full">
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl lg:max-w-3xl mx-auto space-y-8">
        {/* General Settings Section */}
        <div className="space-y-4">
          <div className="space-y-6">
            {/* Render switches dynamically */}
            {FORM_CONFIG.switches.map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between space-x-2 p-4 border rounded-lg bg-white">
                <Label htmlFor={`${key}-switch`} className="flex flex-col items-start space-y-1 cursor-pointer">
                  <span>{label}</span>
                  <span className="font-normal leading-snug text-muted-foreground">{description}</span>
                </Label>
                <Switch id={`${key}-switch`} checked={config[key]} onCheckedChange={updateBooleanField(key)} />
              </div>
            ))}
          </div>
        </div>

        {/* Render checkbox sections dynamically */}
        {FORM_CONFIG.checkboxSections.map(({ key, title, description, options }) => (
          <div key={key} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-1">{title}</h2>
              <p className="text-sm text-muted-foreground mb-4">{description}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
              {options.map((option) => {
                const sectionConfig = config[key] as Record<string, boolean>;
                return (
                  <Label
                    key={option}
                    htmlFor={`${key}-${option}`}
                    className="flex items-start space-x-3 p-3 border rounded-lg bg-background hover:bg-accent hover:text-accent-foreground transition-colors hover:cursor-pointer"
                  >
                    <Checkbox
                      id={`${key}-${option}`}
                      checked={sectionConfig[option] ?? false}
                      onCheckedChange={updateNestedBooleanField(key, option)}
                    />
                    <div className="grid gap-1.5 leading-none">{option}</div>
                  </Label>
                );
              })}
            </div>
          </div>
        ))}

        {/* Auto-approve Section */}
        <div className="space-y-4 pt-4">
          <div>
            <h2 className="text-lg font-semibold mb-1">Auto-approve</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Mesa will automatically approve pull requests if it doesn't find any issues during its review.
            </p>
          </div>

          {/* Enabled Switch */}
          <div className="flex items-center justify-between space-x-2 p-4 border rounded-lg bg-white">
            <Label
              htmlFor="auto-approve-enabled"
              className="flex flex-col items-start space-y-1 cursor-pointer select-none"
            >
              <span>Enable Auto-approve</span>
              <span className="font-normal leading-snug text-muted-foreground">
                Automatically approve pull requests based on thresholds.
              </span>
            </Label>
            <Switch
              id="auto-approve-enabled"
              checked={config.autoApprove.enabled}
              onCheckedChange={(checked) => updateAutoApprove({ enabled: checked })}
            />
          </div>

          {config.autoApprove.enabled && (
            <>
              {/* Labels Input */}
              <div className="flex flex-col gap-2">
                <Label className="text-base font-medium">Trigger Labels</Label>
                <p className="text-sm text-muted-foreground">
                  PRs with these labels will be enabled for auto-approval.
                </p>
                {config.autoApprove.labels.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {config.autoApprove.labels.map((label) => (
                      <Badge
                        key={label}
                        variant="outline"
                        className="cursor-pointer hover:bg-destructive/20 hover:text-destructive-foreground"
                        onClick={() => removeLabel(label)}
                      >
                        {label}
                        <span className="ml-1">×</span>
                      </Badge>
                    ))}
                  </div>
                )}
                <Input
                  id="auto-approve-label-input"
                  placeholder="Add a label (press Enter)"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addLabel(labelInput);
                      setLabelInput('');
                    }
                  }}
                  className="bg-stone-50/75 max-w-sm"
                />
              </div>

              {/* Threshold inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(['low', 'medium', 'high'] as const).map((level) => (
                  <div key={level} className="space-y-2">
                    <Label htmlFor={`threshold-${level}`}>
                      {level.charAt(0).toUpperCase() + level.slice(1)} threshold
                    </Label>
                    <Input
                      id={`threshold-${level}`}
                      type="number"
                      min={0}
                      placeholder="No limit"
                      value={
                        config.autoApprove.threshold[level] === undefined ||
                        config.autoApprove.threshold[level] === null
                          ? ''
                          : String(config.autoApprove.threshold[level])
                      }
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : Number(e.target.value);
                        updateAutoApproveThreshold(level)(val);
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
